import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Map as MapIcon, Eye, Globe2, Search, FilePlus2, Check, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ReconWorkbench } from './ReconWorkbench';

const STATUS_LABEL = { pending: 'To review', good: 'Looks good', needs_work: 'Needs work', skip: 'Skip', appraisal_created: 'Appraisal created', ready: 'Ready for letter' };
const RATINGS = ['Good', 'Fair', 'Poor'];

function ReconRow({ row, services, onSaved }) {
  const { api } = useAuth();
  const [notes, setNotes] = useState(row.recon_notes);
  const [busy, setBusy] = useState('');
  const [wbOpen, setWbOpen] = useState(false);
  const save = async (patch) => {
    setBusy('save');
    try { await api.patch(`/reconnect/recon/${row.property_id}`, patch); onSaved(row.property_id, patch); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };
  const toggleSvc = (s) => {
    const next = row.recon_services.includes(s) ? row.recon_services.filter(x => x !== s) : [...row.recon_services, s];
    save({ services: next });
  };
  const makeAppraisal = async () => {
    setBusy('appr');
    try {
      const { data } = await api.post(`/reconnect/recon/${row.property_id}/appraisal`, { notes, rating: row.recon_rating });
      toast.success(`Drive-by appraisal ${data.appraisal_no || ''} created`);
      onSaved(row.property_id, { status: 'appraisal_created', appraisal_id: data.appraisal_id });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create appraisal'); }
    finally { setBusy(''); }
  };
  const L = row.links || {};
  return (
    <div data-testid={`recon-row-${row.property_id}`} className="border border-[#E2E2DF] rounded-md bg-white p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <p className="font-semibold text-slate-900">{row.address || 'No address'}</p>
          <p className="text-xs text-slate-500">{row.client_name || '—'} · {row.email || 'no email'} · {row.phone || 'no phone'}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {row.jobs} job{row.jobs === 1 ? '' : 's'}{row.completed ? ` · ${row.completed} completed` : ''}
            {row.last_year ? ` · last ${row.last_year}` : ''}{row.last_type ? ` (${row.last_type})` : ''}
            {row.roof_type ? ` · ${row.roof_type}` : ''}{row.area_code ? ` · area ${row.area_code}` : ''}
          </p>
        </div>
        <div className="flex gap-1.5">
          <button data-testid={`recon-workbench-open-${row.property_id}`} onClick={() => setWbOpen(true)} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[#D35400] text-white hover:bg-[#A04000]"><Wrench size={12} />Workbench</button>
          {L.street_view && <a data-testid={`recon-streetview-${row.property_id}`} href={L.street_view} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-50"><Eye size={12} />Street View</a>}
          {L.google_earth && <a data-testid={`recon-earth-${row.property_id}`} href={L.google_earth} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-50"><Globe2 size={12} />Earth</a>}
          {L.google_maps && <a href={L.google_maps} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-50"><MapIcon size={12} />Map</a>}
        </div>
      </div>
      <div className="mt-3 grid md:grid-cols-12 gap-3 items-start">
        <div className="md:col-span-3 flex flex-wrap gap-1">
          {RATINGS.map(r => (
            <button key={r} data-testid={`recon-rating-${r.toLowerCase()}-${row.property_id}`} onClick={() => save({ rating: r, status: r === 'Good' ? 'good' : 'needs_work' })}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${row.recon_rating === r ? 'bg-[#D35400] text-white border-[#D35400]' : 'border-slate-300 hover:bg-slate-50'}`}>{r}</button>
          ))}
          <button data-testid={`recon-skip-${row.property_id}`} onClick={() => save({ status: 'skip' })} className="px-2.5 py-1 text-xs rounded-full border border-slate-300 text-slate-500 hover:bg-slate-50">Skip</button>
        </div>
        <div className="md:col-span-5 flex flex-wrap gap-1">
          {services.map(s => (
            <button key={s} onClick={() => toggleSvc(s)} data-testid={`recon-svc-${s.replace(/\s+/g, '-').toLowerCase()}-${row.property_id}`}
              className={`px-2 py-0.5 text-[11px] rounded border ${row.recon_services.includes(s) ? 'bg-sky-50 border-sky-400 text-sky-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{s}</button>
          ))}
        </div>
        <div className="md:col-span-4 flex gap-2">
          <input data-testid={`recon-notes-${row.property_id}`} value={notes} onChange={e => setNotes(e.target.value)} onBlur={() => notes !== row.recon_notes && save({ notes })}
            placeholder="What you can see (moss, rust, ridges…)" className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded" />
          {row.recon_appraisal_id ? (
            <a href={`/appraisals/${row.recon_appraisal_id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-emerald-50 text-emerald-700 border border-emerald-200"><Check size={12} />Appraisal</a>
          ) : (
            <button data-testid={`recon-create-appraisal-${row.property_id}`} disabled={!!busy} onClick={makeAppraisal}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50">
              {busy === 'appr' ? <Loader2 size={12} className="animate-spin" /> : <FilePlus2 size={12} />}Drive-by
            </button>
          )}
        </div>
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-wider text-slate-400" data-testid={`recon-status-${row.property_id}`}>{STATUS_LABEL[row.recon_status] || row.recon_status}</p>
      {wbOpen && <ReconWorkbench propertyId={row.property_id} onClose={() => setWbOpen(false)} onStatus={(s) => onSaved(row.property_id, { status: s })} />}
    </div>
  );
}

export function ReconQueue() {
  const { api } = useAuth();
  const [f, setF] = useState({ status: 'pending', q: '', suburb: '', completed_only: true, has_email: '' });
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(0);
  const reqId = useRef(0);
  const load = useCallback(async () => {
    const my = ++reqId.current;
    const { data: d } = await api.get('/reconnect/recon', { params: { ...f, skip, limit: 25 } });
    if (my === reqId.current) setData(d);  // ignore stale responses
  }, [api, f, skip]);
  useEffect(() => { load().catch(() => toast.error('Could not load the recon queue')); }, [load]);
  const onSaved = (pid, patch) => setData(d => {
    const old = d.items.find(r => r.property_id === pid);
    const counts = { ...d.counts };
    if (patch.status && old && patch.status !== old.recon_status) {
      counts[old.recon_status] = Math.max(0, (counts[old.recon_status] || 0) - 1);
      counts[patch.status] = (counts[patch.status] || 0) + 1;
    }
    return { ...d, counts, items: d.items.map(r => r.property_id !== pid ? r : {
    ...r, ...(patch.status ? { recon_status: patch.status } : {}), ...(patch.rating ? { recon_rating: patch.rating } : {}),
    ...(patch.notes !== undefined ? { recon_notes: patch.notes } : {}), ...(patch.services ? { recon_services: patch.services } : {}),
    ...(patch.appraisal_id ? { recon_appraisal_id: patch.appraisal_id } : {}) }) };
  });
  const page = (n) => { setSkip(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const set = (k, v) => { setSkip(0); setF(x => ({ ...x, [k]: v })); };
  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  return (
    <div data-testid="recon-queue" className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        {['pending', 'needs_work', 'good', 'skip', 'appraisal_created', 'ready', 'all'].map(s => (
          <button key={s} data-testid={`recon-filter-${s}`} onClick={() => set('status', s)}
            className={`px-3 py-1 text-xs rounded-full border ${f.status === s ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 hover:bg-slate-50'}`}>
            {s === 'all' ? 'All' : STATUS_LABEL[s]}{s !== 'all' ? ` · ${data.counts[s] || 0}` : ''}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative"><Search size={13} className="absolute left-2 top-2 text-slate-400" />
          <input data-testid="recon-search" value={f.q} onChange={e => set('q', e.target.value)} placeholder="Name, address, email" className="pl-7 pr-2 py-1.5 text-xs border border-slate-300 rounded w-56" /></div>
        <select data-testid="recon-suburb" value={f.suburb} onChange={e => set('suburb', e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded">
          <option value="">All suburbs</option>{data.suburbs.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="recon-email-filter" value={f.has_email} onChange={e => set('has_email', e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded">
          <option value="">Email or not</option><option value="yes">Has email</option><option value="no">No email</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600"><input data-testid="recon-completed-only" type="checkbox" checked={f.completed_only} onChange={e => set('completed_only', e.target.checked)} />Past completed work only</label>
        <span className="ml-auto text-xs text-slate-500" data-testid="recon-total">{data.total} properties</span>
      </div>
      {data.items.map(r => <ReconRow key={r.property_id} row={r} services={data.services} onSaved={onSaved} />)}
      {data.items.length === 0 && <p className="text-sm text-slate-500 p-6 text-center">Nothing here.</p>}
      <div className="flex justify-between">
        <button data-testid="recon-prev" disabled={skip === 0} onClick={() => page(Math.max(0, skip - 25))} className="px-3 py-1 text-xs border rounded disabled:opacity-40">Previous</button>
        <span className="text-xs text-slate-500">{data.total ? `${skip + 1}–${Math.min(skip + 25, data.total)} of ${data.total}` : ''}</span>
        <button data-testid="recon-next" disabled={skip + 25 >= data.total} onClick={() => page(skip + 25)} className="px-3 py-1 text-xs border rounded disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}
